import { useCallback, useEffect, useRef, useState } from "react";

import { usePostHog } from "@posthog/react";
import { useAtom } from "jotai";
import { toast } from "sonner";

import { navigate, routes, useLocation } from "@redwoodjs/router";
import { useMutation } from "@redwoodjs/web";

import RefreshButton from "src/components/Analytics/RefreshButton";
import { StoreAssistantModal } from "src/components/AssistantChat/StoreAssistantModal";
import {
  isStoreSyncPausedForBilling,
  StoreSyncPausedNotice,
} from "src/components/Billing/StoreSyncPausedNotice";
import ContactDialog from "src/components/ContactDialog/ContactDialog";
import SyncingIndicator from "src/components/DashboardRenderer/SyncingIndicator";
import { MarketingIntegrationNotice } from "src/components/Notifications/MarketingIntegrationNotice";
import { NotificationsBell } from "src/components/Notifications/NotificationsBell";
import { OnboardingChecklistButton } from "src/components/Onboarding/OnboardingChecklistCard";
import { OnboardingPageNotice } from "src/components/Onboarding/OnboardingPageNotice";
import PrerenderLoading from "src/components/PrerenderLoading/PrerenderLoading";
import SettingsDialog from "src/components/SettingsDialog/SettingsDialog";
import { AppSidebar } from "src/components/Sidebar/AppSidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "src/components/ui/breadcrumb";
import { Button } from "src/components/ui/button";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "src/components/ui/responsive-dialog";
import { Separator } from "src/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "src/components/ui/sidebar";
import { useAuth } from "src/lib/auth";
import { storeIntegrationTypes } from "src/lib/constants";
import {
  JOIN_DEMO_STORE_EVENT,
  notifyDemoStoreMembershipChanged,
} from "src/lib/demoStore";
import useDashboard from "src/lib/hooks/useDashboard";
import { useIntegration } from "src/lib/hooks/useIntegration";
import { JOIN_DEMO_STORE_MUTATION } from "src/lib/queries/integrations";
import { cn } from "src/lib/utils";

const DashboardLayout = ({ children }) => {
  const postHog = usePostHog();
  const { loading, isAuthenticated, currentUser } = useAuth();
  const [, setSelectedStoreId] = useAtom(
    storeIntegrationTypes.shopify.selectedAtom
  );
  const { pathname, search, searchParams } = useLocation();
  const { refetch: refetchStores } = useIntegration(
    storeIntegrationTypes.shopify,
    {
      queryOnRender: false,
    }
  );
  const [demoWelcomeOpen, setDemoWelcomeOpen] = useState(false);
  const [joiningDemoStoreFlow, setJoiningDemoStoreFlow] = useState(false);
  const joinDemoRequestInFlightRef = useRef(false);
  const joinDemoToastIdRef = useRef(null);

  const [joinDemoStore, { loading: joiningDemoStore }] = useMutation(
    JOIN_DEMO_STORE_MUTATION,
    {
      onCompleted: async ({ joinDemoStore }) => {
        setSelectedStoreId(joinDemoStore.id);

        try {
          await refetchStores();

          setDemoWelcomeOpen(true);

          toast.success("Demo store added", {
            description:
              "You can explore generated commerce and marketing data now.",
            id: joinDemoToastIdRef.current,
          });
        } catch (error) {
          toast.error("Demo store joined", {
            description:
              error?.message ||
              "Refresh the page if the demo store does not appear.",
            id: joinDemoToastIdRef.current,
          });
        } finally {
          notifyDemoStoreMembershipChanged();
          joinDemoRequestInFlightRef.current = false;
          setJoiningDemoStoreFlow(false);
        }
      },
      onError: (error) => {
        joinDemoRequestInFlightRef.current = false;
        setJoiningDemoStoreFlow(false);
        toast.error("Unable to join demo store", {
          description: error?.message || "Please try again.",
          id: joinDemoToastIdRef.current,
        });
      },
    }
  );

  const startJoinDemoStore = useCallback(() => {
    if (joinDemoRequestInFlightRef.current || joiningDemoStore) {
      return;
    }

    joinDemoRequestInFlightRef.current = true;
    setJoiningDemoStoreFlow(true);
    joinDemoToastIdRef.current = toast.loading("Joining demo store...");
    joinDemoStore();
  }, [joinDemoStore, joiningDemoStore]);

  useEffect(() => {
    postHog.identify(currentUser?.id, {
      email: currentUser?.email,
    });
  }, [currentUser, postHog]);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate(routes.login({ redirectTo: pathname + search }));
    }
  }, [loading, isAuthenticated, pathname, search]);

  useEffect(() => {
    if (loading || !isAuthenticated) {
      return;
    }

    if (searchParams.get("settings")) {
      return;
    }

    const storeId = searchParams.get("store");
    if (!storeId) {
      return;
    }

    setSelectedStoreId(storeId);

    const nextSearchParams = new URLSearchParams(search);
    nextSearchParams.delete("store");

    const nextSearch = nextSearchParams.toString();
    navigate(nextSearch ? `${pathname}?${nextSearch}` : pathname, {
      replace: true,
    });
  }, [
    isAuthenticated,
    loading,
    pathname,
    search,
    searchParams,
    setSelectedStoreId,
  ]);

  useEffect(() => {
    if (loading || !isAuthenticated) {
      return;
    }

    if (searchParams.get("demo") !== "join") {
      return;
    }

    startJoinDemoStore();

    const nextSearchParams = new URLSearchParams(search);
    nextSearchParams.delete("demo");
    const nextSearch = nextSearchParams.toString();

    navigate(nextSearch ? `${pathname}?${nextSearch}` : pathname, {
      replace: true,
    });
  }, [
    isAuthenticated,
    loading,
    pathname,
    search,
    searchParams,
    startJoinDemoStore,
  ]);

  useEffect(() => {
    const handleJoinDemoStore = () => {
      startJoinDemoStore();
    };

    window.addEventListener(JOIN_DEMO_STORE_EVENT, handleJoinDemoStore);

    return () => {
      window.removeEventListener(JOIN_DEMO_STORE_EVENT, handleJoinDemoStore);
    };
  }, [startJoinDemoStore]);

  if (
    loading ||
    !isAuthenticated ||
    joiningDemoStore ||
    joiningDemoStoreFlow ||
    searchParams.get("demo") === "join"
  ) {
    return <PrerenderLoading />;
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>{children}</SidebarInset>

      <SettingsDialog />
      <ContactDialog />
      <StoreAssistantModal />
      <ResponsiveDialog
        open={demoWelcomeOpen}
        onOpenChange={setDemoWelcomeOpen}
      >
        <ResponsiveDialogContent className="md:max-w-xl">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              Welcome to the demo store
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Explore live Shopify, Meta Ads, and Google Ads sample data. The
              demo is read-only, and you can leave it from store settings at any
              time.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <ResponsiveDialogFooter>
            <Button onClick={() => setDemoWelcomeOpen(false)}>
              Start exploring
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </SidebarProvider>
  );
};

// Sidebar toggle, breadcrumbs/title, export, etc
const Header = ({ className, children, onRefresh, loading }) => {
  const { breadcrumb } = useDashboard();
  const { pathname } = useLocation();
  const { selectedIntegration } = useIntegration(
    storeIntegrationTypes.shopify,
    {
      queryOnRender: false,
    }
  );
  const showBillingPauseBadge =
    !pathname.startsWith("/admin") &&
    isStoreSyncPausedForBilling(selectedIntegration);

  return (
    <header
      className={cn(
        "bg-background sticky top-0 z-50 mb-4 flex h-12 shrink-0 items-center gap-2 overflow-hidden rounded-t-xl border-b p-4",
        className
      )}
    >
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />

      <Breadcrumb className="min-w-0">
        <BreadcrumbList className="min-w-0 flex-nowrap overflow-hidden">
          {breadcrumb.map((item, index) => {
            const isLast = index === breadcrumb.length - 1;
            const Comp = isLast ? BreadcrumbPage : BreadcrumbLink;

            return (
              <BreadcrumbItem
                key={item.url}
                className={cn(isLast ? "min-w-0 flex-1" : "shrink-0")}
              >
                <Comp
                  to={item.url}
                  className={cn(
                    isLast ? "block max-w-full truncate font-bold" : "shrink-0"
                  )}
                >
                  {item.name}
                </Comp>
                {!isLast && <BreadcrumbSeparator className="shrink-0" />}
              </BreadcrumbItem>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>

      {children}

      {showBillingPauseBadge && (
        <StoreSyncPausedNotice
          store={selectedIntegration}
          className="ml-2"
          side="bottom"
          align="start"
        />
      )}

      <div className="ml-auto flex items-center gap-2">
        <NotificationsBell />
        <SyncingIndicator />
        <OnboardingChecklistButton />
        {onRefresh && (
          <RefreshButton
            onClick={onRefresh}
            loading={loading}
            className="-mr-2"
          />
        )}
      </div>
    </header>
  );
};
DashboardLayout.Header = Header;

DashboardLayout.Main = ({ children, className }) => (
  <div
    className={cn(
      "@container/main flex flex-1 flex-col gap-4 px-4 pb-4 sm:px-8 md:gap-6 md:pb-6",
      className
    )}
  >
    <MarketingIntegrationNotice />
    <OnboardingPageNotice />
    {children}
  </div>
);

DashboardLayout.Filters = ({ children, className }) => (
  <div className={cn("flex flex-wrap items-center gap-2", className)}>
    {children}
  </div>
);

DashboardLayout.Cards = ({ children, className }) => (
  <div
    className={cn(
      "*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs",
      className
    )}
  >
    {children}
  </div>
);

DashboardLayout.SectionTitle = ({ children, className }) => (
  <div className={cn("text-lg font-semibold tracking-wide", className)}>
    {children}
  </div>
);

DashboardLayout.TableOptions = ({ children, className }) => (
  <div
    className={cn(
      "flex flex-wrap items-center justify-between gap-4",
      className
    )}
  >
    {children}
  </div>
);

DashboardLayout.Graph = ({ children }) => <div className="">{children}</div>;

export default DashboardLayout;
